import { useState, useEffect, useCallback } from 'react';
import { BrowserProvider, Contract, JsonRpcProvider, JsonRpcSigner, parseEther, formatEther } from 'ethers';
import { TERNOA_CHAIN, STAKING_CONTRACT, STAKING_ABI } from '@/config/contracts';

interface StakingState {
  stakedAmount: string; // in CAPS (human-readable)
  hasStaked: boolean;
  lastStakeTimestamp: number;
  loading: boolean;
  txPending: boolean;
  txHash: string | null;
  error: string | null;
}

const KNOWN_ERRORS: Record<string, string> = {
  '0x7cf7f469': 'Stake amount is below the minimum (1,000 CAPS)',
};

const readProvider = new JsonRpcProvider(TERNOA_CHAIN.rpcUrl);

function getReadContract() {
  return new Contract(STAKING_CONTRACT.address, STAKING_ABI, readProvider);
}

/** Ensure the wallet is on Ternoa chain, then return a fresh signer */
async function ensureTernoaChainAndGetSigner(): Promise<JsonRpcSigner> {
  if (!window.ethereum) throw new Error('No wallet detected');

  // Check current chain
  const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
  const currentChainId = parseInt(chainIdHex, 16);

  if (currentChainId !== TERNOA_CHAIN.chainId) {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: TERNOA_CHAIN.chainIdHex }],
      });
    } catch (switchError: any) {
      if (switchError.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: TERNOA_CHAIN.chainIdHex,
            chainName: TERNOA_CHAIN.name,
            nativeCurrency: TERNOA_CHAIN.nativeCurrency,
            rpcUrls: [TERNOA_CHAIN.rpcUrl],
            blockExplorerUrls: [TERNOA_CHAIN.explorerUrl],
          }],
        });
      } else {
        throw new Error('Please switch to Ternoa zkEVM network');
      }
    }
  }

  // Always create a fresh provider+signer after chain switch
  const provider = new BrowserProvider(window.ethereum, TERNOA_CHAIN.chainId);
  return provider.getSigner();
}

function parseContractError(err: any): string {
  // Check for known custom error selectors
  const data = err?.data || err?.error?.data;
  if (typeof data === 'string') {
    const selector = data.slice(0, 10);
    if (KNOWN_ERRORS[selector]) return KNOWN_ERRORS[selector];
  }

  // Check error message for the selector
  const msg = err?.message || '';
  for (const [sel, desc] of Object.entries(KNOWN_ERRORS)) {
    if (msg.includes(sel.slice(2))) return desc;
  }

  return err?.reason || err?.shortMessage || msg || 'Transaction failed';
}

export function useLegacyStaking(address: string | null, signer: JsonRpcSigner | null) {
  const [state, setState] = useState<StakingState>({
    stakedAmount: '0',
    hasStaked: false,
    lastStakeTimestamp: 0,
    loading: false,
    txPending: false,
    txHash: null,
    error: null,
  });

  const loadStakeInfo = useCallback(async () => {
    if (!address) return;
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const contract = getReadContract();
      const [stakeAmount, hasStaked, lastStakeTimestamp] = await contract.getStakeInfo(address);
      setState(s => ({
        ...s,
        stakedAmount: formatEther(stakeAmount),
        hasStaked,
        lastStakeTimestamp: Number(lastStakeTimestamp),
        loading: false,
      }));
    } catch (err: any) {
      setState(s => ({ ...s, loading: false, error: 'Failed to load staking info' }));
    }
  }, [address]);

  useEffect(() => {
    loadStakeInfo();
  }, [loadStakeInfo]);

  const stake = useCallback(async (amountCaps: string) => {
    if (!signer) return;
    setState(s => ({ ...s, txPending: true, txHash: null, error: null }));

    try {
      const freshSigner = await ensureTernoaChainAndGetSigner();
      const contract = new Contract(STAKING_CONTRACT.address, STAKING_ABI, freshSigner);
      const value = parseEther(amountCaps);
      const tx = await contract.stake({ value });
      setState(s => ({ ...s, txHash: tx.hash }));
      await tx.wait();
      setState(s => ({ ...s, txPending: false }));
      await loadStakeInfo();
    } catch (err: any) {
      setState(s => ({ ...s, txPending: false, error: parseContractError(err) }));
    }
  }, [signer, loadStakeInfo]);

  const unstake = useCallback(async (amountCaps: string) => {
    if (!signer) return;
    setState(s => ({ ...s, txPending: true, txHash: null, error: null }));

    try {
      const freshSigner = await ensureTernoaChainAndGetSigner();
      const contract = new Contract(STAKING_CONTRACT.address, STAKING_ABI, freshSigner);
      const amount = parseEther(amountCaps);
      const tx = await contract.unstake(amount);
      setState(s => ({ ...s, txHash: tx.hash }));
      await tx.wait();
      setState(s => ({ ...s, txPending: false }));
      await loadStakeInfo();
    } catch (err: any) {
      setState(s => ({ ...s, txPending: false, error: parseContractError(err) }));
    }
  }, [signer, loadStakeInfo]);

  const clearError = useCallback(() => {
    setState(s => ({ ...s, error: null }));
  }, []);

  const clearTxHash = useCallback(() => {
    setState(s => ({ ...s, txHash: null }));
  }, []);

  return {
    ...state,
    stake,
    unstake,
    refresh: loadStakeInfo,
    clearError,
    clearTxHash,
  };
}
